import React from 'react';
import { Campaign } from '../../types/campaign';
import { formatCurrency } from '../../utils/formatters';
import { Building2, Calendar, Target, Users } from 'lucide-react';

interface CampaignListProps {
  campaigns: Campaign[];
  loading: boolean;
  onEdit: (campaign: Campaign) => void;
  onDelete: (campaignId: string) => void;
}

export default function CampaignList({ campaigns, loading, onEdit, onDelete }: CampaignListProps) {
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-2 text-gray-500">Loading campaigns...</p>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        No campaigns found. Create a new campaign to get started.
      </div>
    );
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="divide-y divide-gray-200">
      {campaigns.map((campaign) => (
        <div key={campaign.id} className="p-4 hover:bg-gray-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h4 className="text-lg font-medium text-gray-900">{campaign.name}</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusClass(campaign.status)}`}>
                  {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                </span>
              </div>
              
              <p className="mt-1 text-sm text-gray-500">{campaign.description}</p>
              
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Target className="w-4 h-4 mr-2" />
                  {campaign.metrics.totalTargets} targets
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Users className="w-4 h-4 mr-2" />
                  {campaign.metrics.contacted} contacted
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Building2 className="w-4 h-4 mr-2" />
                  {formatCurrency(campaign.budget)}
                </div>
              </div>
            </div>
            
            <div className="ml-4 flex space-x-2">
              <button
                onClick={() => onEdit(campaign)}
                className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(campaign.id)}
                className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}